import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowDown, ArrowUp, Flag, Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, qs } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KpiCard, SectionTitle } from "@/components/Kpi";
import { AzzeraFiltri, FilterBar, useFiltersMeta } from "@/components/Filters";
import { eurShort, num, perc } from "@/lib/format";

const ANNO = 2026;
const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const A1 = String(ANNO - 1).slice(2);
const A0 = String(ANNO).slice(2);
const ddmm = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : "");
const eurInt = (v) => (v ? num(v) : "–");
const intOrDash = (v) => (v ? num(v) : "–");

const COLONNE_FINALI = [
  { key: "tot_n_prec", label: `TOT PRT ${ANNO - 1}`, fmt: intOrDash },
  { key: "progr_n_prec", label: `PROGR PRT ${ANNO - 1}`, fmt: intOrDash },
  { key: "progr_n_cur", label: `PROGR PRT ${ANNO}`, fmt: intOrDash, forte: true },
  { key: "tot_imp_prec", label: `TOT ${ANNO - 1}`, fmt: eurInt },
  { key: "progr_imp_prec", label: `PROGR ${ANNO - 1}`, fmt: eurInt },
  { key: "progr_imp_cur", label: `PROGR ${ANNO}`, fmt: eurInt, forte: true },
  { key: "scostamento_perc", label: "SCOSTAMENTO", fmt: null },
  { key: "proiezione_imp", label: `PROIEZIONE ${ANNO}`, fmt: eurInt, forte: true },
];

/** Analisi Portafoglio: pratiche e importi per convenzionato, mese per mese, 2026 vs 2025. */
export default function AnalisiPortafoglio() {
  const { slug } = useParams();
  const { user } = useAuth();
  const meta = useFiltersMeta();
  const [filters, setFilters] = useState({ anno: ANNO, ...(slug ? { commerciale: slug } : {}) });
  const [tipo, setTipo] = useState("caricato");
  const [d, setD] = useState(null);
  const [caricamento, setCaricamento] = useState(false);
  const [cerca, setCerca] = useState("");
  const [mostraMesi, setMostraMesi] = useState(true);
  const [soloRossi, setSoloRossi] = useState(false);
  const [sort, setSort] = useState({ key: "progr_imp_prec", desc: true });
  const [reset, setReset] = useState(0);
  const puoSegnare = user?.role !== "COMMERCIALE" && user?.role !== "VISITATORE";

  useEffect(() => { setFilters((f) => ({ ...f, commerciale: slug || "" })); }, [slug]);
  useEffect(() => {
    setCaricamento(true);
    api.get(`/analisi-portafoglio?${qs({ ...filters, tipo })}`).then((r) => setD(r.data))
      .catch((e) => { setD(null); toast.error(apiError(e)); }).finally(() => setCaricamento(false));
  }, [filters, tipo]);

  const righe = useMemo(() => {
    let r = d?.righe || [];
    const q = cerca.trim().toLowerCase();
    if (q) r = r.filter((x) => (x.ragione_sociale || "").toLowerCase().includes(q) || String(x.codice).includes(q));
    if (soloRossi) r = r.filter((x) => x.rosso);
    const s = [...r].sort((a, b) => {
      const va = a[sort.key] ?? -Infinity, vb = b[sort.key] ?? -Infinity;
      if (typeof va === "string") return sort.desc ? vb.localeCompare(va) : va.localeCompare(vb);
      return sort.desc ? vb - va : va - vb;
    });
    return s;
  }, [d, cerca, soloRossi, sort]);

  const ordina = (key) => setSort((s) => ({ key, desc: s.key === key ? !s.desc : true }));
  const toggleRosso = async (r) => {
    if (!puoSegnare) return;
    try {
      await api.put(`/analisi-portafoglio/rosso/${r.codice}`, { rosso: !r.rosso });
      setD((x) => ({ ...x, righe: x.righe.map((y) => (y.codice === r.codice ? { ...y, rosso: !r.rosso } : y)),
        kpi: { ...x.kpi, rossi: x.kpi.rossi + (r.rosso ? -1 : 1) } }));
    } catch (e) { toast.error(apiError(e)); }
  };

  const t = d?.totali;
  const cut = ddmm(d?.cutoff_precedente);
  const Sc = ({ v, bold }) => (
    <span className={`font-mono ${bold ? "font-bold" : "font-semibold"} ${v == null ? "text-slate-300"
      : v >= 0 ? "text-emerald-700" : v >= -10 ? "text-amber-600" : "text-red-600"}`}>{v == null ? "–" : perc(v, 1)}</span>
  );
  const Th = ({ k, children, className = "", ...rest }) => (
    <th {...rest} onClick={k ? () => ordina(k) : undefined}
      className={`px-2 py-1.5 text-[10px] uppercase tracking-wide font-semibold text-slate-600 whitespace-nowrap
        ${k ? "cursor-pointer hover:text-[#0018A8] select-none" : ""} ${className}`}>
      <span className="inline-flex items-center gap-0.5">{children}
        {k && sort.key === k && (sort.desc ? <ArrowDown size={10} /> : <ArrowUp size={10} />)}</span>
    </th>
  );
  const celleMesi = (r, bold) => r.mesi.map((m, i) => (
    <MeseCelle key={i} m={m} bold={bold} pari={i % 2 === 0} />
  ));
  const celleFinali = (r, bold) => COLONNE_FINALI.map((c) => (
    <td key={c.key} className={`px-2 py-1.5 text-right font-mono whitespace-nowrap border-l border-slate-100
      ${c.forte ? "bg-blue-50/60" : ""} ${bold || c.forte ? "font-semibold" : ""}`}>
      {c.fmt ? c.fmt(r[c.key]) : <Sc v={r[c.key]} bold={bold} />}
    </td>
  ));

  return (
    <div data-testid="analisi-portafoglio-page">
      <SectionTitle sub={`Pratiche e importi ${tipo === "caricato" ? "caricati" : "liquidati"} per convenzionato, mese per mese · ${ANNO} vs ${ANNO - 1}${
        d?.ultima_data ? ` · dati al ${ddmm(d.ultima_data)}/${ANNO} (progressivo ${ANNO - 1} a pari data, al ${cut})` : ""}`}
        right={
          <a href={`/api/analisi-portafoglio.xlsx?${qs({ ...filters, tipo })}`} data-testid="btn-export-excel"
            className="px-4 py-2 rounded-md bg-[#0018A8] text-white text-sm hover:bg-[#001480] transition-colors duration-200">
            Esporta Excel
          </a>
        }>
        Analisi Portafoglio {ANNO}
      </SectionTitle>

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-md border border-slate-200 bg-white overflow-hidden" data-testid="ap-switch-tipo">
          {["caricato", "liquidato"].map((k) => (
            <button key={k} type="button" onClick={() => setTipo(k)} data-testid={`ap-tipo-${k}`}
              className={`px-4 py-2 text-sm font-semibold capitalize transition-colors duration-150
                ${tipo === k ? (k === "caricato" ? "bg-[#0018A8] text-white" : "bg-[#C8102E] text-white") : "text-slate-600 hover:bg-slate-50"}`}>
              {k}
            </button>
          ))}
        </div>
        <FilterBar key={reset} meta={meta} value={filters} onChange={setFilters}
          fields={slug ? ["settore"] : ["commerciale", "settore"]} />
        <label className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="Cerca convenzionato…"
            data-testid="ap-cerca"
            className="pl-7 pr-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:border-[#0018A8] w-56" />
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
          <input type="checkbox" checked={mostraMesi} onChange={(e) => setMostraMesi(e.target.checked)}
            data-testid="ap-mostra-mesi" className="accent-[#0018A8]" /> Dettaglio mesi
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
          <input type="checkbox" checked={soloRossi} onChange={(e) => setSoloRossi(e.target.checked)}
            data-testid="ap-solo-rossi" className="accent-[#C8102E]" /> Solo in rosso
        </label>
        <AzzeraFiltri onClick={() => {
          setFilters({ anno: ANNO, ...(slug ? { commerciale: slug } : { commerciale: "" }) });
          setCerca(""); setSoloRossi(false); setReset((v) => v + 1);
        }} />
      </div>

      {!d ? (
        <div className="text-sm text-slate-500 mt-6" data-testid="ap-loading">{caricamento ? "Caricamento…" : "Nessun dato."}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 xl:grid-cols-5 gap-4 my-6">
            <KpiCard testId="ap-kpi-progr-prec" label={`Progressivo ${ANNO - 1} · al ${cut}`} value={t.progr_imp_prec}
              kind="eurShort" sub={`${num(t.progr_n_prec)} pratiche · anno intero ${eurShort(t.tot_imp_prec)}`} />
            <KpiCard testId="ap-kpi-progr-cur" label={`Progressivo ${ANNO}`} value={t.progr_imp_cur} kind="eurShort"
              accent={tipo} sub={`${num(t.progr_n_cur)} pratiche`} />
            <KpiCard testId="ap-kpi-scostamento" label="Scostamento importo" value={t.scostamento_perc} kind="perc"
              accent={t.scostamento_perc == null ? "slate" : t.scostamento_perc >= 0 ? "positivo" : "negativo"}
              sub={`Pratiche ${perc(t.scostamento_n_perc, 1)}`} />
            <KpiCard testId="ap-kpi-proiezione" label={`Proiezione ${ANNO}`} value={t.proiezione_imp} kind="eurShort"
              accent="sky" sub={`${num(t.proiezione_n)} pratiche · ${d.giorni_lavorati}/${d.giorni_lavorativi_anno} gg lavorativi`} />
            <KpiCard testId="ap-kpi-convenzionati" label="Convenzionati" value={d.kpi.convenzionati}
              sub={`${num(d.kpi.attivi_cur)} attivi ${ANNO} · ${num(d.kpi.attivi_prec)} nel ${ANNO - 1}`}
              note={`${num(d.kpi.rossi)} segnalati in rosso`} />
          </div>

          <div className="bg-white border border-slate-200 shadow-sm rounded-lg overflow-hidden">
            <div className="overflow-auto max-h-[70vh]" data-testid="ap-tabella-wrap">
              <table className="text-xs border-collapse min-w-full" data-testid="ap-tabella">
                <thead className="sticky top-0 z-20 bg-slate-50 shadow-[0_1px_0_#e2e8f0]">
                  {mostraMesi && (
                    <tr className="bg-slate-100">
                      <th className="sticky left-0 z-30 bg-slate-100" colSpan={puoSegnare ? 5 : 4} />
                      {MESI.map((m, i) => (
                        <th key={m} colSpan={4} className={`px-2 py-1 text-[10px] uppercase tracking-[0.14em] font-bold
                          text-[#0018A8] border-l border-slate-200 ${i % 2 === 0 ? "bg-slate-100" : "bg-slate-50"}`}>{m}</th>
                      ))}
                      <th colSpan={COLONNE_FINALI.length} className="px-2 py-1 text-[10px] uppercase tracking-[0.14em] font-bold text-slate-700 border-l border-slate-200">Totali e progressivi</th>
                    </tr>
                  )}
                  <tr>
                    {puoSegnare && <Th className="sticky left-0 z-30 bg-slate-50 w-8" />}
                    <Th k="codice" className={`sticky z-30 bg-slate-50 ${puoSegnare ? "left-8" : "left-0"}`}>Codice</Th>
                    <Th k="ragione_sociale" className={`sticky z-30 bg-slate-50 text-left ${puoSegnare ? "left-[6.5rem]" : "left-[4.5rem]"}`}>Ragione sociale</Th>
                    <Th k="settore">Settore</Th>
                    <Th k="commerciale_nome">Commerciale</Th>
                    {mostraMesi && MESI.map((m, i) => (
                      ["N " + A1, "€ " + A1, "N " + A0, "€ " + A0].map((l, j) => (
                        <Th key={`${m}-${j}`} className={`text-right ${j === 0 ? "border-l border-slate-200" : ""}
                          ${i % 2 === 0 ? "bg-slate-100/70" : ""} ${j >= 2 ? "text-[#0018A8]" : ""}`}>{l}</Th>
                      ))
                    ))}
                    {COLONNE_FINALI.map((c) => (
                      <Th key={c.key} k={c.key} className={`text-right border-l border-slate-100 ${c.forte ? "bg-blue-50/60" : ""}`}>
                        {c.label}{c.key === "progr_n_prec" || c.key === "progr_imp_prec" ? ` (${cut})` : ""}
                      </Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-slate-100 font-semibold text-slate-900 border-b-2 border-slate-300" data-testid="ap-riga-totale">
                    {puoSegnare && <td className="sticky left-0 z-10 bg-slate-100" />}
                    <td className={`sticky z-10 bg-slate-100 px-2 py-2 ${puoSegnare ? "left-8" : "left-0"}`} />
                    <td className={`sticky z-10 bg-slate-100 px-2 py-2 ${puoSegnare ? "left-[6.5rem]" : "left-[4.5rem]"}`}>TOTALE {d.commerciale_nome ? d.commerciale_nome.toUpperCase() : ""}</td>
                    <td /><td />
                    {mostraMesi && celleMesi(t, true)}
                    {celleFinali(t, true)}
                  </tr>
                  {righe.map((r) => (
                    <tr key={r.codice} data-testid={`ap-riga-${r.codice}`}
                      className={`border-b border-slate-100 transition-colors duration-100 ${r.rosso
                        ? "bg-red-50 text-red-800 hover:bg-red-100" : "hover:bg-slate-50 text-slate-800"}`}>
                      {puoSegnare && (
                        <td className={`sticky left-0 z-10 px-1.5 ${r.rosso ? "bg-red-50" : "bg-white"}`}>
                          <button type="button" onClick={() => toggleRosso(r)} data-testid={`ap-rosso-${r.codice}`}
                            title={r.rosso ? "Togli evidenza rossa" : "Segna in rosso"}
                            className={`p-1 rounded transition-colors duration-150 ${r.rosso ? "text-[#C8102E]" : "text-slate-300 hover:text-[#C8102E]"}`}>
                            <Flag size={12} fill={r.rosso ? "#C8102E" : "none"} />
                          </button>
                        </td>
                      )}
                      <td className={`sticky z-10 px-2 py-1.5 font-mono ${puoSegnare ? "left-8" : "left-0"} ${r.rosso ? "bg-red-50" : "bg-white"}`}>{r.codice}</td>
                      <td className={`sticky z-10 px-2 py-1.5 font-medium whitespace-nowrap max-w-[260px] truncate ${puoSegnare ? "left-[6.5rem]" : "left-[4.5rem]"} ${r.rosso ? "bg-red-50" : "bg-white"}`}
                        title={r.ragione_sociale}>{r.ragione_sociale}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-slate-500">{r.settore || "–"}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-slate-500">{r.commerciale_nome}</td>
                      {mostraMesi && celleMesi(r, false)}
                      {celleFinali(r, false)}
                    </tr>
                  ))}
                  {righe.length === 0 && (
                    <tr><td colSpan={60} className="px-4 py-8 text-center text-slate-400">Nessun convenzionato corrisponde ai filtri.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 text-[11px] text-slate-400 border-t border-slate-100 flex flex-wrap gap-x-4">
              <span>{num(righe.length)} convenzionati mostrati</span>
              <span>Progressivo {ANNO - 1} = stesse date del {ANNO} (fino al {cut}) · Proiezione = progressivo / {d.giorni_lavorati} gg lavorati × {d.giorni_lavorativi_anno} gg lavorativi</span>
              <span>Il convenzionato segue il commerciale attuale dell'anagrafica</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MeseCelle({ m, bold, pari }) {
  const bg = pari ? "bg-slate-50/60" : "";
  const c = `px-2 py-1.5 text-right font-mono whitespace-nowrap ${bg} ${bold ? "font-semibold" : ""}`;
  return (
    <>
      <td className={`${c} border-l border-slate-200 text-slate-500`}>{intOrDash(m.n_prec)}</td>
      <td className={`${c} text-slate-500`}>{eurInt(m.imp_prec)}</td>
      <td className={`${c} text-[#0018A8]`}>{intOrDash(m.n_cur)}</td>
      <td className={`${c} text-[#0018A8]`}>{eurInt(m.imp_cur)}</td>
    </>
  );
}
